import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string[];
  ogImage?: string;
  ogType?: string;
  canonicalUrl?: string;
  publishDate?: string;
  modifiedDate?: string;
  author?: string;
  structuredData?: object | object[];
  noIndex?: boolean;
  markdownUrl?: string;
}

// ========================================
// KEYWORD STRATEGY — PME + IA + France
// Cibles : dirigeants PME, directeurs data,
// directeurs marketing, DAF, DG
// ========================================
const CORE_KEYWORDS = [
  // Requêtes primaires (volume élevé)
  'IA pour PME',
  'intelligence artificielle PME',
  'IA business intelligence',
  'analyse données PME',
  'BI PME France',
  'outil IA entreprise',
  // Requêtes intentionnelles (forte conversion)
  'analyser données entreprise IA',
  'IA analyse Excel automatique',
  'outil IA analyse données PME',
  'logiciel intelligence artificielle PME',
  'plateforme IA données entreprise',
  // Requêtes longue traîne (faible concurrence)
  'détecter patterns données business',
  'IA mémoire persistante analyse',
  'multi-agents IA données PME',
  'prédiction churn clients PME IA',
  'IA détection contradictions business',
  'optimiser décisions data-driven PME',
  'cloud souverain IA France RGPD',
  'alternative Power BI IA PME',
  'alternative Tableau IA française',
  // Requêtes RGPD/souveraineté (différenciateur)
  'IA hébergée en France',
  'IA conforme RGPD PME',
  'cloud souverain Scaleway IA',
  'données entreprise France souveraineté',
  // Requêtes verticales
  'IA analyse financière PME',
  'IA prévision trésorerie',
  'IA optimisation stock PME',
  'IA prédiction ventes PME',
  'IA marketing PME segmentation',
  'audit données entreprise',
  'augmenter chiffre d\'affaires PME',
  'insights business cachés',
  'patterns business IA',
  'rentabilité par client',
  'CA hausse marge baisse',
];

export default function SEOHead({
  title = 'Clairify — IA Business Intelligence pour PME | Analyse de données intelligente',
  description = 'Clairify est l\'IA qui comprend votre business. Mémoire persistante, détection de contradictions, système multi-agents. Analysez vos données et prenez des décisions 3x plus rapides. Hébergé en France, conforme RGPD. Essai gratuit.',
  keywords = CORE_KEYWORDS,
  ogImage = 'https://clairify.fr/og-image.png',
  ogType = 'website',
  canonicalUrl,
  publishDate,
  modifiedDate,
  author = 'Équipe Clairify',
  structuredData,
  noIndex = false,
  markdownUrl,
}: SEOHeadProps) {
  const location = useLocation();
  const finalCanonical = canonicalUrl || `https://clairify.fr${location.pathname}`;

  // ========================================
  // SCHEMA: Organization (always present)
  // ========================================
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': 'https://clairify.fr/#organization',
    name: 'Clairify',
    legalName: 'STELLARMEET SAS',
    description: 'Solution IA Business Intelligence pour PME françaises. Analyse de données avec mémoire persistante, détection de patterns et contradictions.',
    url: 'https://clairify.fr',
    logo: {
      '@type': 'ImageObject',
      url: 'https://clairify.fr/logo-light.svg',
      width: 512,
      height: 512,
    },
    image: 'https://clairify.fr/og-image.png',
    foundingDate: '2024-10-30',
    founders: [{ '@type': 'Person', name: 'Fondateurs Clairify' }],
    address: {
      '@type': 'PostalAddress',
      streetAddress: '25 rue de Ponthieu',
      addressLocality: 'Paris',
      postalCode: '75008',
      addressRegion: 'Île-de-France',
      addressCountry: 'FR',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 48.8738,
      longitude: 2.3086,
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'contact@clairify.fr',
        availableLanguage: ['French'],
        areaServed: 'FR',
      },
      {
        '@type': 'ContactPoint',
        contactType: 'technical support',
        email: 'support@clairify.fr',
        availableLanguage: ['French'],
      },
    ],
    sameAs: [
      'https://linkedin.com/company/clairify',
      'https://twitter.com/clairify_ai',
    ],
    vatID: 'FR11935065094',
    taxID: '935065094',
    numberOfEmployees: { '@type': 'QuantitativeValue', value: '1-10' },
    areaServed: {
      '@type': 'Country',
      name: 'France',
    },
    knowsAbout: [
      'Business Intelligence',
      'Intelligence Artificielle',
      'Analyse de données',
      'Machine Learning',
      'PME',
      'Data Analytics',
      'RGPD',
    ],
  };

  // ========================================
  // SCHEMA: SoftwareApplication (rich result)
  // ========================================
  const softwareSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    '@id': 'https://clairify.fr/#software',
    name: 'Clairify',
    description: 'Plateforme IA d\'analyse de données business pour PME avec mémoire persistante, détection de patterns et système multi-agents.',
    url: 'https://clairify.fr',
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'Business Intelligence',
    operatingSystem: 'Web Browser',
    offers: [
      {
        '@type': 'Offer',
        name: 'Gratuit',
        price: '0',
        priceCurrency: 'EUR',
        description: '50 000 tokens/mois gratuits',
        availability: 'https://schema.org/InStock',
      },
      {
        '@type': 'Offer',
        name: 'Pro',
        price: '29',
        priceCurrency: 'EUR',
        billingIncrement: 'P1M',
        description: '960 000 tokens/mois, Deep Reports, Pattern Detection',
        availability: 'https://schema.org/InStock',
      },
      {
        '@type': 'Offer',
        name: 'Business',
        price: '79',
        priceCurrency: 'EUR',
        billingIncrement: 'P1M',
        description: '2.6M tokens/mois, Support prioritaire, Memory avancée',
        availability: 'https://schema.org/InStock',
      },
      {
        '@type': 'Offer',
        name: 'Enterprise',
        price: '199',
        priceCurrency: 'EUR',
        billingIncrement: 'P1M',
        description: '6.6M tokens/mois, Multi-utilisateurs, Support 24/7',
        availability: 'https://schema.org/InStock',
      },
    ],
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      bestRating: '5',
      ratingCount: '47',
      reviewCount: '32',
    },
    featureList: [
      'Analyse de données CSV, Excel, JSON, PDF',
      'Chat IA en langage naturel',
      'Mémoire persistante (Memory Layer)',
      'Détection de patterns récurrents',
      'Pattern Collisions (détection de contradictions)',
      'Système multi-agents IA',
      'DNA Score (maturité business)',
      'Deep Reports PDF automatiques',
      'Intégrations Notion et Slack',
      'Hébergement souverain France (Scaleway)',
      'Conforme RGPD',
      'Chiffrement AES-256',
    ],
    screenshot: 'https://clairify.fr/og-image.png',
    softwareVersion: '2.0',
    releaseNotes: 'Memory Layer v2, Pattern Collisions, Multi-Agent System',
    author: { '@id': 'https://clairify.fr/#organization' },
    provider: { '@id': 'https://clairify.fr/#organization' },
    inLanguage: 'fr',
    isAccessibleForFree: true,
    license: 'https://clairify.fr/terms',
    privacyPolicy: 'https://clairify.fr/privacy',
  };

  // ========================================
  // SCHEMA: WebSite (sitelinks searchbox)
  // ========================================
  const webSiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': 'https://clairify.fr/#website',
    name: 'Clairify',
    description: 'IA Business Intelligence pour PME',
    url: 'https://clairify.fr',
    publisher: { '@id': 'https://clairify.fr/#organization' },
    inLanguage: 'fr',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://clairify.fr/blog?q={search_term_string}',
      },
      'query-input': 'required name=search_term_string',
    },
  };

  // ========================================
  // SCHEMA: BreadcrumbList
  // ========================================
  const pathParts = location.pathname.split('/').filter(Boolean);
  const breadcrumbItems = [
    { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://clairify.fr/' },
  ];
  if (pathParts.length > 0) {
    const pageNames: Record<string, string> = {
      pricing: 'Tarifs', blog: 'Blog', about: 'À propos', contact: 'Contact',
      login: 'Connexion', studio: 'Studio', settings: 'Paramètres',
      privacy: 'Confidentialité', terms: 'CGU', cookies: 'Cookies', legal: 'Mentions légales',
    };
    let currentPath = '';
    pathParts.forEach((part, i) => {
      currentPath += `/${part}`;
      breadcrumbItems.push({
        '@type': 'ListItem',
        position: i + 2,
        name: pageNames[part] || part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, ' '),
        item: `https://clairify.fr${currentPath}`,
      });
    });
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems,
  };

  const pageSchemas = structuredData
    ? (Array.isArray(structuredData) ? structuredData : [structuredData])
    : [softwareSchema, webSiteSchema];
  const allSchemas = [organizationSchema, breadcrumbSchema, ...pageSchemas];

  return (
    <Helmet>
      {/* ===== CORE META ===== */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(', ')} />
      <meta name="author" content={author} />
      <link rel="canonical" href={finalCanonical} />
      <link rel="describedby" href="https://clairify.fr/llms.txt" />
      <link rel="alternate" type="text/plain" title="LLM index" href="https://clairify.fr/llms.txt" />
      <link rel="alternate" type="text/plain" title="LLM index alias" href="https://clairify.fr/llm.txt" />
      {markdownUrl && (
        <link rel="alternate" type="text/markdown" href={markdownUrl} />
      )}

      {/* ===== ROBOTS ===== */}
      <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'} />
      <meta name="googlebot" content={noIndex ? 'noindex' : 'index, follow, max-snippet:-1, max-image-preview:large'} />
      <meta name="bingbot" content={noIndex ? 'noindex' : 'index, follow'} />

      {/* ===== OPENGRAPH ===== */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={finalCanonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Clairify - IA Business Intelligence pour PME" />
      <meta property="og:site_name" content="Clairify" />
      <meta property="og:locale" content="fr_FR" />

      {/* ===== TWITTER ===== */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@clairify_ai" />
      <meta name="twitter:creator" content="@clairify_ai" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* ===== GEO & LANGUAGE ===== */}
      <meta name="language" content="fr" />
      <meta httpEquiv="content-language" content="fr" />
      <meta name="geo.region" content="FR-75" />
      <meta name="geo.placename" content="Paris, France" />
      <meta name="geo.position" content="48.8738;2.3086" />
      <meta name="ICBM" content="48.8738, 2.3086" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      <meta name="revisit-after" content="3 days" />
      <link rel="alternate" hrefLang="fr" href={finalCanonical} />
      <link rel="alternate" hrefLang="x-default" href={finalCanonical} />

      {/* ===== ARTICLE (blog posts) ===== */}
      {publishDate && <meta property="article:published_time" content={publishDate} />}
      {modifiedDate && <meta property="article:modified_time" content={modifiedDate} />}
      {publishDate && <meta property="article:author" content={author} />}
      {publishDate && <meta property="article:section" content="Business Intelligence" />}
      {publishDate && <meta property="article:tag" content="IA PME" />}
      {publishDate && <meta property="article:tag" content="Business Intelligence" />}
      {publishDate && <meta property="article:tag" content="Analyse données" />}

      {/* ===== STRUCTURED DATA ===== */}
      {allSchemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}

      {/* ===== PERFORMANCE ===== */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="dns-prefetch" href="https://zpeiwjlhybkomxebhlqr.supabase.co" />
      <link rel="dns-prefetch" href="https://clairifybackendaqetdhg6-container-nostalgic-swanson.functions.fnc.fr-par.scw.cloud" />
    </Helmet>
  );
}
